import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const PIANO_KEYS = [
  { note: 'C4', label: 'Do', color: 'white', freq: 261.63 },
  { note: 'D4', label: 'Re', color: 'white', freq: 293.66 },
  { note: 'E4', label: 'Mi', color: 'white', freq: 329.63 },
  { note: 'F4', label: 'Fa', color: 'white', freq: 349.23 },
  { note: 'G4', label: 'Sol', color: 'white', freq: 392.00 },
  { note: 'A4', label: 'La', color: 'white', freq: 440.00 },
  { note: 'B4', label: 'Si', color: 'white', freq: 493.88 },
  { note: 'C5', label: 'Do', color: 'white', freq: 523.25 }
];

const DRUM_PADS = [
  { label: '🥁', name: 'Kick', color: '#FF6B6B', type: 'kick' },
  { label: '🎵', name: 'Snare', color: '#4ECDC4', type: 'snare' },
  { label: '🔔', name: 'Hi-hat', color: '#FECA57', type: 'hihat' },
  { label: '💥', name: 'Crash', color: '#45B7D1', type: 'crash' },
  { label: '🎶', name: 'Tom 1', color: '#A55EEA', type: 'tom1' },
  { label: '🎸', name: 'Tom 2', color: '#26de81', type: 'tom2' }
];

let audioCtx = null;

function getAudioContext() {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  return audioCtx;
}

function playTone(freq, duration = 0.5) {
  const ctx = getAudioContext();
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(freq, ctx.currentTime);
  gainNode.gain.setValueAtTime(0.5, ctx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + duration);
}

function playDrum(type) {
  const ctx = getAudioContext();
  const bufferSize = ctx.sampleRate * 0.3;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);

  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 3);
  }

  const source = ctx.createBufferSource();
  const gainNode = ctx.createGain();
  source.buffer = buffer;
  source.connect(gainNode);
  gainNode.connect(ctx.destination);

  const freqMap = { kick: 0.6, snare: 0.3, hihat: 0.1, crash: 0.2, tom1: 0.5, tom2: 0.4 };
  gainNode.gain.setValueAtTime(freqMap[type] || 0.3, ctx.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
  source.start();
}

export default function MusicScreen() {
  const navigate = useNavigate();
  const [activeInstrument, setActiveInstrument] = React.useState('piano');
  const [activeKeys, setActiveKeys] = React.useState(new Set());

  const handlePianoKey = useCallback((key) => {
    playTone(key.freq);
    setActiveKeys(prev => new Set([...prev, key.note]));
    setTimeout(() => setActiveKeys(prev => {
      const next = new Set(prev);
      next.delete(key.note);
      return next;
    }), 300);
  }, []);

  const handleDrum = useCallback((pad) => {
    playDrum(pad.type);
  }, []);

  return (
    <div className="screen">
      <div className="screen-header" style={{ background: 'linear-gradient(135deg, #45AAF2, #2980B9)', paddingBottom: 16 }}>
        <button className="btn-back" onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.3)', color: 'white' }}>←</button>
        <h1 style={{ fontFamily: 'var(--font-title)', color: 'white', fontSize: '1.5rem' }}>🎵 Music</h1>
      </div>

      {/* Instrument Selector */}
      <div style={{ display: 'flex', gap: 10, padding: '16px 20px' }}>
        {[['piano', '🎹 Piano'], ['drums', '🥁 Drums']].map(([id, label]) => (
          <button key={id} onClick={() => setActiveInstrument(id)} style={{
            flex: 1, padding: '12px', border: '3px solid',
            borderColor: activeInstrument === id ? 'var(--blue)' : 'var(--border)',
            borderRadius: 14, background: activeInstrument === id ? '#E3F2FD' : 'white',
            fontFamily: 'var(--font-body)', fontWeight: 800, cursor: 'pointer', fontSize: '1rem'
          }}>{label}</button>
        ))}
      </div>

      <div className="screen-body" style={{ paddingTop: 10 }}>
        {activeInstrument === 'piano' ? (
          <div>
            <p style={{ color: 'var(--gray)', fontSize: '0.85rem', marginBottom: 16, textAlign: 'center' }}>
              Tap the keys to make music! 🎵
            </p>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
              {PIANO_KEYS.map(key => (
                <button
                  key={key.note}
                  onPointerDown={() => handlePianoKey(key)}
                  style={{
                    flex: 1, aspectRatio: '1/3', minHeight: 140,
                    borderRadius: '0 0 12px 12px', border: '2px solid #ddd',
                    background: activeKeys.has(key.note)
                      ? 'linear-gradient(180deg, #667eea, #764ba2)'
                      : 'white',
                    cursor: 'pointer', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'flex-end', padding: '0 0 8px',
                    fontSize: '0.65rem', fontWeight: 700, color: activeKeys.has(key.note) ? 'white' : 'var(--gray)',
                    transition: 'all 0.1s', boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                    transform: activeKeys.has(key.note) ? 'scaleY(0.97)' : 'none'
                  }}
                >
                  {key.label}
                </button>
              ))}
            </div>
            <div className="card" style={{ background: '#E3F2FD', border: '2px solid var(--blue)', marginTop: 20 }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--dark)' }}>
                🎵 <strong>Try this:</strong> Play Do-Re-Mi-Fa-Sol-La-Si-Do to hear the musical scale!
              </p>
            </div>
          </div>
        ) : (
          <div>
            <p style={{ color: 'var(--gray)', fontSize: '0.85rem', marginBottom: 16, textAlign: 'center' }}>
              Tap the pads to beat the drums! 🥁
            </p>
            <div className="grid-2" style={{ gap: 12 }}>
              {DRUM_PADS.map(pad => (
                <button
                  key={pad.type}
                  onPointerDown={() => handleDrum(pad)}
                  style={{
                    padding: '24px 16px', border: 'none',
                    borderRadius: 20, background: pad.color,
                    cursor: 'pointer', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: 6,
                    boxShadow: `0 6px 0 ${pad.color}88`,
                    transition: 'all 0.1s', fontFamily: 'var(--font-body)',
                    fontWeight: 800, color: 'white', fontSize: '0.9rem'
                  }}
                  onPointerUp={e => e.currentTarget.style.transform = 'none'}
                  onPointerLeave={e => e.currentTarget.style.transform = 'none'}
                >
                  <span style={{ fontSize: '2rem' }}>{pad.label}</span>
                  {pad.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
